import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Clock, GraduationCap } from "lucide-react";

// Public page reached from a make-up seat-offer DM (WhatsApp/Telegram/Instagram):
//   /makeup/claim/:token
// First "views" the offer (child, programme, when) via make-up-claim, then lets
// the parent confirm, which books the child into the offered slot atomically.

type Offer = {
  child_name: string;
  level: string | null;
  programme: string;
  date: string | null;
  time: string | null;
};

type ClaimState =
  | { kind: "loading" }
  | { kind: "offer"; offer: Offer }
  | { kind: "confirmed"; bookingId?: string }
  | { kind: "unavailable"; message: string };

function prettyDate(d: string | null): string {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
  });
}

export default function MakeUpClaim() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<ClaimState>({ kind: "loading" });
  const [claiming, setClaiming] = useState(false);

  // Pull the edge function's JSON body even on a non-2xx response.
  const readError = async (error: unknown, fallback: string): Promise<string> => {
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx) {
        const body = await ctx.json();
        if (body?.error) return body.error as string;
      }
    } catch { /* keep fallback */ }
    return fallback;
  };

  // Load the offer details on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState({ kind: "unavailable", message: "This make-up link is invalid." });
        return;
      }
      try {
        const { data, error } = await supabase.functions.invoke("make-up-claim", {
          body: { offer_token: token },
        });
        if (cancelled) return;
        if (error) {
          setState({ kind: "unavailable", message: await readError(error, "This make-up link is no longer valid.") });
          return;
        }
        if (data?.mode === "offer") {
          setState({ kind: "offer", offer: data as Offer });
        } else {
          setState({ kind: "unavailable", message: data?.error || "This make-up offer is no longer available." });
        }
      } catch (e) {
        if (!cancelled) setState({ kind: "unavailable", message: (e as Error).message });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const claim = useCallback(async () => {
    if (!token) return;
    setClaiming(true);
    try {
      const { data, error } = await supabase.functions.invoke("make-up-claim", {
        body: { offer_token: token, action: "claim" },
      });
      if (error) {
        setState({ kind: "unavailable", message: await readError(error, "That session may have just filled up.") });
        return;
      }
      if (data?.mode === "confirmed") {
        setState({ kind: "confirmed", bookingId: data.booking_id });
        return;
      }
      setState({ kind: "unavailable", message: data?.error || "This make-up offer is no longer available." });
    } catch (e) {
      setState({ kind: "unavailable", message: (e as Error).message });
    } finally {
      setClaiming(false);
    }
  }, [token]);

  // Auto-redirect a confirmed booking to its confirmation page after a beat.
  useEffect(() => {
    if (state.kind === "confirmed" && state.bookingId) {
      const t = setTimeout(() => navigate(`/booking-confirmed/${state.bookingId}`), 1800);
      return () => clearTimeout(t);
    }
  }, [state, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md bg-background rounded-2xl shadow-sm border p-8 text-center">
        {state.kind === "loading" && (
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
        )}

        {state.kind === "offer" && (
          <>
            <GraduationCap className="w-10 h-10 mx-auto mb-4 text-primary" />
            <h1 className="text-xl font-semibold mb-2">Make-up class available</h1>
            <p className="text-muted-foreground mb-5">
              A seat just opened for <span className="font-medium text-foreground">{state.offer.child_name}</span>
              {state.offer.level ? ` (${state.offer.level})` : ""} in {state.offer.programme}.
            </p>
            <div className="rounded-xl border bg-muted/40 p-4 mb-6 text-sm">
              <div className="font-medium">{prettyDate(state.offer.date)}</div>
              {state.offer.time && <div className="text-muted-foreground">{state.offer.time}</div>}
            </div>
            <Button className="w-full" size="lg" onClick={claim} disabled={claiming}>
              {claiming ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Claiming…</>
              ) : (
                <>Claim this make-up</>
              )}
            </Button>
            <p className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" /> This offer is time-limited — first come, first served.
            </p>
          </>
        )}

        {state.kind === "confirmed" && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold mb-2">Make-up booked! 🎉</h1>
            <p className="text-muted-foreground">Taking you to the confirmation…</p>
          </>
        )}

        {state.kind === "unavailable" && (
          <>
            <h1 className="text-xl font-semibold mb-2">Make-up no longer available</h1>
            <p className="text-muted-foreground mb-6">{state.message}</p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/">Back to home</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
